import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useParams, useSearchParams } from "react-router";
import { Skeleton } from "@/components/skeleton";
import { cn } from "@/lib/utils";
import { useTaskList, useTaskAccountability, completeTask, createTask, skipTask, updateTask, fetchTaskDetail, fetchTaskHistory } from "@/hooks/use-tasks";
import { EmptyState } from "@/components/ui/empty-state";
import {
  CrmTaskActionGroupsPanel,
  CrmTaskExecutionChecklistsPanel,
  CrmTaskPlaybooksPanel,
  CrmTaskProfileSectionsPanel,
} from "@/components/crm/task-guidance-panels";
import { CrmTaskOperatorPreviewCard } from "@/components/crm/task-operator-preview-card";
import { CrmTaskAccountabilityPanel } from "@/components/crm/task-accountability-panel";
import { CrmTaskStatusBanner } from "@/components/crm/task-status-banner";
import {
  CrmTaskReviewFiltersPanel,
  CrmTaskStatusFilterTabs,
} from "@/components/crm/task-review-filters-panel";
import { CrmTaskCompletionToggleButton, CrmTaskRowActions } from "@/components/crm/task-row-actions";
import { CrmTaskRowFacts } from "@/components/crm/task-row-facts";
import { CrmTaskHistoryPanel } from "@/components/crm/task-history-panel";
import {
  CrmTaskAssigneeRoutingCard,
  CrmTaskContextQualityCard,
  getCrmTaskContextCompletenessStatus,
} from "@/components/crm/task-detail-state-cards";
import {
  CrmTaskAutomationStatusCard,
  CrmTaskSlaEscalationPanel,
  getCrmTaskSlaDetail,
  getCrmTaskSlaTone,
} from "@/components/crm/task-automation-sla-cards";
import {
  CrmTaskCompletionContextCard,
  CrmTaskCompletionDetailsCard,
  CrmTaskContextSnapshotCard,
  type CrmTaskDetailEntry,
  CrmTaskMetadataHighlightsCard,
  CrmTaskOperationalDetailsCard,
  CrmTaskOperatorBriefCard,
  CrmTaskWhyExistsCard,
} from "@/components/crm/task-detail-panels";
import {
  CrmTaskCompletionFollowUpPanel,
  CrmTaskCompletionOutcomeFieldsPanel,
  isCrmTaskCompletionFieldRequiredForOutcome,
} from "@/components/crm/task-completion-form-panels";
import {
  CrmTaskCreateBasicsPanel,
  CrmTaskCreateRelatedRecordsPanel,
  CrmTaskCreateSchedulePanel,
} from "@/components/crm/task-create-form-panels";
import {
  CrmTaskDialogErrorBanner,
  CrmTaskDialogFooter,
  CrmTaskDialogShell,
  CrmTaskSkipReasonPanel,
} from "@/components/crm/task-dialog-panels";
import { useTerminology } from "@/lib/terminology";
import { formatMonthDay } from "@/lib/i18n";
import { useContactList } from "@/hooks/use-contacts";
import { useCompanyList } from "@/hooks/use-companies";
import { useDealList } from "@/hooks/use-deals";
import { useProposals, type Proposal } from "@/hooks/use-proposals";
import { useOrganizationMembers } from "@/hooks/use-organization-members";
import type { OrganizationMemberRecord } from "@/lib/organization-client";
import { CRM_UNASSIGNED_ASSIGNEE_FILTER_VALUE } from "@ease/crm/types";
import {
  CRM_TASK_REGISTRY_OPTIONS,
  getCrmTaskCompletionOutcomeDefinitions,
  getCrmTaskDefinition,
  normalizeCrmTaskType,
} from "@ease/shared/crm-task-registry";
import type { CrmTaskCompletionFieldDefinition } from "@ease/shared/crm-task-registry";
import {
  buildCrmTaskActionGroups,
  buildCrmTaskContactChannelActionGroup,
  buildCrmTaskContactChannelActions,
  CRM_TASK_ACTION_GROUP_FIELD_KEYS,
  CRM_TASK_CONTACT_CHANNEL_FIELD_KEYS,
  type CrmTaskAction,
  type CrmTaskActionGroup,
} from "@ease/crm/task-action-groups";
import {
  buildCrmTaskProfileSections,
  CRM_TASK_PROFILE_SECTION_FIELD_KEYS,
  summarizeCrmTaskProfileSections,
} from "@ease/crm/task-profile-sections";
import {
  buildCrmTaskExecutionChecklists,
  CRM_TASK_EXECUTION_CHECKLIST_FIELD_KEYS,
  summarizeCrmTaskExecutionChecklists,
} from "@ease/crm/task-execution-checklist";
import {
  buildCrmTaskPlaybooks,
  CRM_TASK_PLAYBOOK_FIELD_KEYS,
  summarizeCrmTaskPlaybooks,
} from "@ease/crm/task-playbooks";
import type {
  CompleteTaskInput,
  TaskCompletionFollowUpType,
  TaskLifecycleEventSummary,
  TaskRecurrenceFrequency,
  TaskSummary,
} from "@/hooks/use-tasks";
type FilterStatus = "all" | "pending" | "in_progress" | "completed" | "skipped" | "cancelled";
type RecurrenceOption = "none" | TaskRecurrenceFrequency;
type DueFilter = "all" | "overdue" | "next_7_days" | "future";
const TASK_HISTORY_PAGE_LIMIT = 10;

const STATUS_LABELS: Record<FilterStatus, string> = {
  all: "All",
  pending: "Pending",
  in_progress: "In progress",
  completed: "Completed",
  skipped: "Skipped",
  cancelled: "Cancelled",
};
const STATUS_FILTER_OPTIONS = (["pending", "in_progress", "completed", "skipped", "cancelled", "all"] as const)
  .map((value) => ({ value, label: STATUS_LABELS[value] }));

const RECURRENCE_LABELS: Record<TaskRecurrenceFrequency, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly: "Monthly",
};

const DUE_FILTER_LABELS: Record<DueFilter, string> = {
  all: "Any due date",
  overdue: "Overdue",
  next_7_days: "Due in 7 days",
  future: "Future due",
};
const DUE_FILTER_OPTIONS = (Object.entries(DUE_FILTER_LABELS) as Array<[DueFilter, string]>)
  .map(([value, label]) => ({ value, label }));
const TASK_TYPE_FILTER_OPTIONS = CRM_TASK_REGISTRY_OPTIONS
  .map((entry) => ({ value: entry.type, label: entry.label }));

const MANUAL_TASK_TYPE_OPTIONS = CRM_TASK_REGISTRY_OPTIONS.filter((entry) => entry.manualCreatable);
const CREATE_TASK_TYPE_OPTIONS = MANUAL_TASK_TYPE_OPTIONS
  .map((entry) => ({ value: entry.type, label: entry.label }));

const COMPLETION_FOLLOW_UP_TYPES = MANUAL_TASK_TYPE_OPTIONS
  .map((entry) => ({ value: entry.type as TaskCompletionFollowUpType, label: entry.label }));
const FOLLOW_UP_SUGGESTION_DELAY_MS = 24 * 60 * 60 * 1000;
const COMPLETION_FOLLOW_UP_PRESETS = [
  { label: "Tomorrow", days: 1 },
  { label: "In 2 days", days: 2 },
  { label: "Next week", days: 7 },
] as const;

const CONTEXT_METADATA_KEYS = new Set([
  "actionRef",
  "autoCancelReason",
  "breakReturnDueAt",
  "cefrLevel",
  "channel",
  "clientName",
  "companyName",
  "contactName",
  "customerName",
  "dealTitle",
  "reason",
  "rationale",
  "summary",
  "intent",
  "intentClass",
  "learnerName",
  "recommendationReason",
  "routingReason",
  "priorityReason",
  "offerTitle",
  "offerType",
  "offerStatus",
  "offerValue",
  "paymentStatus",
  "personName",
  "campaign",
  "sourceEntityId",
  "sourceEntityType",
  "trigger",
  "triggerType",
  "sourceEventId",
  "sourceEventType",
  "confidence",
  "score",
  "scoreBand",
  "leadTier",
  "productQualification",
  "qualificationGate",
  "stage",
  "renewalAtRisk",
  "healthAlerts",
  "daysUntilRenewal",
  "riskReasonCode",
  "diagnosticScore",
  "lessonsCompleted",
  "currentStreakDays",
  "cadenceDueAt",
  "streakDays",
  "streakBrokenDays",
  "plateauTopic",
  "topStruggle",
  "consultationStatus",
  "consultationIntent",
  "expectedCloseDate",
  "expectedConversionDate",
  "feedbackMilestoneDueAt",
  "installmentDueAt",
  "offerExpiresAt",
  "planClassificationRequired",
  "promiseDueAt",
  "resolutionEventType",
  "resolutionEventId",
]);

const HIDDEN_TASK_SURFACE_KEYS = new Set<string>([
  ...CRM_TASK_PROFILE_SECTION_FIELD_KEYS,
  ...CRM_TASK_ACTION_GROUP_FIELD_KEYS,
  ...CRM_TASK_CONTACT_CHANNEL_FIELD_KEYS,
  ...CRM_TASK_EXECUTION_CHECKLIST_FIELD_KEYS,
  ...CRM_TASK_PLAYBOOK_FIELD_KEYS,
]);

const PRIVATE_TASK_CONTEXT_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;

const DUE_CONTEXT_FIELDS: Array<{ key: string; label: string }> = [
  { key: "promiseDueAt", label: "Promise due" },
  { key: "installmentDueAt", label: "Installment due" },
  { key: "offerExpiresAt", label: "Offer expires" },
  { key: "breakReturnDueAt", label: "Break return due" },
  { key: "feedbackMilestoneDueAt", label: "Feedback milestone due" },
  { key: "cadenceDueAt", label: "Cadence due" },
  { key: "expectedCloseDate", label: "Expected close" },
  { key: "expectedConversionDate", label: "Expected conversion" },
];

function getTaskRecurrenceLabel(metadata: Record<string, unknown> | null | undefined): string | null {
  const recurrence = metadata?.recurrence;
  if (!recurrence || typeof recurrence !== "object") {
    return null;
  }

  const frequency = (recurrence as Record<string, unknown>).frequency;
  return frequency === "daily" || frequency === "weekly" || frequency === "biweekly" || frequency === "monthly"
    ? RECURRENCE_LABELS[frequency]
    : null;
}

function formatTaskLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatMetadataValue(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const values = value.map(formatMetadataValue).filter(Boolean);
    return values.length > 0 ? values.join(", ") : null;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const namedValue = formatMetadataValue(record.label)
      ?? formatMetadataValue(record.name)
      ?? formatMetadataValue(record.title);
    if (namedValue) return namedValue;

    const pairs = Object.entries(record)
      .map(([key, item]) => {
        const formatted = typeof item === "string" ? formatTaskLabel(item) : formatMetadataValue(item);
        return formatted ? `${formatTaskLabel(key) ?? key}: ${formatted}` : null;
      })
      .filter(Boolean);

    return pairs.length > 0 ? pairs.join("; ") : null;
  }
  return null;
}

function formatDueContextValue(value: unknown): string | null {
  if (typeof value === "string" || value instanceof Date) {
    const date = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(date.getTime())) return formatMonthDay(date);
  }
  return formatMetadataValue(value);
}

function getInitialCompletionFieldValues(task: TaskSummary): Record<string, string> {
  const details = task.metadata?.completionDetails;
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return {};
  }

  return Object.entries(details as Record<string, unknown>).reduce<Record<string, string>>((acc, [key, value]) => {
    const formatted = formatMetadataValue(value);
    if (formatted) acc[key] = formatted;
    return acc;
  }, {});
}

function getVisibleCompletionFields(
  fields: readonly CrmTaskCompletionFieldDefinition[],
  outcome: string,
): CrmTaskCompletionFieldDefinition[] {
  return fields.filter((field) => (
    !field.requiredOutcomes || (field.requiredOutcomes as readonly string[]).includes(outcome)
  ));
}

function sanitizeCompletionDetails(
  values: Record<string, string>,
  fields: readonly CrmTaskCompletionFieldDefinition[],
): Record<string, string> {
  const allowedKeys = new Set(fields.map((field) => field.key));
  return Object.entries(values).reduce<Record<string, string>>((acc, [key, value]) => {
    const trimmed = value.trim();
    if (allowedKeys.has(key) && trimmed.length > 0) acc[key] = trimmed;
    return acc;
  }, {});
}

function deriveOutcomeFromCompletionDetails(outcome: string, details: Record<string, string>): string {
  return details.callOutcome === "no_answer" ? "no_answer" : outcome;
}

function getSuggestedFollowUpDateInput(): string {
  return getFollowUpDateInputForDays(1);
}

function getFollowUpDateInputForDays(days: number): string {
  return new Date(Date.now() + days * FOLLOW_UP_SUGGESTION_DELAY_MS).toISOString().slice(0, 10);
}

function readTaskContext(task: TaskSummary): string | null {
  if (task.operatorReadModel?.contextSummary?.trim()) {
    return task.operatorReadModel.contextSummary.trim();
  }

  const metadata = task.metadata ?? {};
  const dueReason = readTaskDueReason(task);
  return task.description?.trim()
    || task.notes?.trim()
    || formatMetadataValue(metadata.reason)
    || formatMetadataValue(metadata.rationale)
    || formatMetadataValue(metadata.recommendationReason)
    || formatMetadataValue(metadata.routingReason)
    || formatTaskLabel(formatMetadataValue(metadata.intent) ?? "")
    || dueReason?.value
    || null;
}

function readTaskGuidance(task: TaskSummary): string | null {
  if (task.operatorReadModel?.guidance?.trim()) {
    return task.operatorReadModel.guidance.trim();
  }

  if (task.scriptTemplate?.trim()) {
    return task.scriptTemplate.trim();
  }

  const nextAction = formatTaskLabel(task.nextAction) ?? task.nextAction;
  if (nextAction) {
    return `Recommended next action: ${nextAction}`;
  }

  if (task.followUpDate) {
    return `Follow up on ${formatMonthDay(task.followUpDate)}`;
  }

  const dueReason = readTaskDueReason(task);
  if (dueReason) {
    return `Review ${dueReason.value.toLowerCase()} and record the outcome.`;
  }

  if (task.template === "crm.task_create@v1" || task.sourceRuleId === "stero.crm_action_execution_queue.v1") {
    return "Review this CRM recommendation, contact the linked person if appropriate, and record the result.";
  }

  return null;
}

function readFormattedRecordValue(
  records: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
): string | null {
  for (const record of records) {
    if (!record) continue;
    for (const key of keys) {
      const formatted = formatMetadataValue(record[key]);
      if (formatted) return formatted;
    }
  }
  return null;
}

function getTaskContextRecords(task: TaskSummary): Array<Record<string, unknown>> {
  return [
    task.metadata ?? {},
    task.contextSnapshot ?? {},
  ];
}

function readTaskTargetLabel(task: TaskSummary): string | null {
  const records = getTaskContextRecords(task);
  const personName = readFormattedRecordValue(records, [
    "contactName",
    "personName",
    "customerName",
    "clientName",
    "learnerName",
    "studentName",
  ]);
  const companyName = readFormattedRecordValue(records, ["companyName", "accountName", "organizationName"]);
  const dealTitle = readFormattedRecordValue(records, ["dealTitle", "opportunityTitle"]);
  const offerTitle = readFormattedRecordValue(records, ["offerTitle", "proposalTitle"]);

  if (personName && companyName) return `${personName} (${companyName})`;
  if (personName) return personName;
  if (dealTitle) return `Deal: ${dealTitle}`;
  if (companyName) return `Company: ${companyName}`;
  if (offerTitle) return `Offer: ${offerTitle}`;
  return null;
}

function readTaskContactLinkLabel(task: TaskSummary): string | null {
  return readFormattedRecordValue(getTaskContextRecords(task), [
    "contactName",
    "personName",
    "customerName",
    "clientName",
    "learnerName",
    "studentName",
  ]);
}

function readTaskDealLinkLabel(task: TaskSummary): string | null {
  return readFormattedRecordValue(getTaskContextRecords(task), ["dealTitle", "opportunityTitle"]);
}

function readTaskCompanyLinkLabel(task: TaskSummary): string | null {
  return readFormattedRecordValue(getTaskContextRecords(task), ["companyName", "accountName", "organizationName"]);
}

function readTaskSignalLabel(task: TaskSummary): string | null {
  const records = getTaskContextRecords(task);
  const signal = task.triggerType
    ?? readFormattedRecordValue(records, ["sourceEventType", "triggerType", "trigger", "sourceRuleId"])
    ?? task.sourceRuleId;
  return formatTaskLabel(signal) ?? signal;
}

function readTaskScoreIntentLabel(task: TaskSummary): string | null {
  const records = getTaskContextRecords(task);
  const score = readFormattedRecordValue(records, ["leadScore", "score", "diagnosticScore", "healthScore"]);
  const intent = readFormattedRecordValue(records, ["consultationIntent", "intentClass"])
    ?? readFormattedRecordValue(records, ["intent"]);
  const scoreBand = readFormattedRecordValue(records, ["scoreBand", "leadTier", "scoreTier"]);
  const qualification = readFormattedRecordValue(records, ["productQualification", "salesQualification"]);
  const qualificationGate = readFormattedRecordValue(records, ["qualificationGate"]);
  const parts = [
    score ? `Score ${score}` : null,
    intent ? (formatTaskLabel(intent) ?? intent) : null,
    scoreBand ? (formatTaskLabel(scoreBand) ?? scoreBand) : null,
    qualification ? (formatTaskLabel(qualification) ?? qualification) : null,
    qualificationGate ? (formatTaskLabel(qualificationGate) ?? qualificationGate) : null,
  ].filter((entry): entry is string => Boolean(entry));

  return parts.length > 0 ? Array.from(new Set(parts)).join(" · ") : null;
}

function readTaskDueReason(task: TaskSummary): { label: string; value: string } | null {
  const records = getTaskContextRecords(task);

  for (const record of records) {
    if (record.planClassificationRequired === true) {
      return { label: "Due reason", value: "Plan classification required" };
    }
    if (record.renewalAtRisk === true) {
      const reason = formatMetadataValue(record.riskReasonCode);
      return { label: "Due reason", value: reason ? `Renewal at risk: ${formatTaskLabel(reason) ?? reason}` : "Renewal at risk" };
    }
    const healthAlerts = formatMetadataValue(record.healthAlerts);
    if (healthAlerts && healthAlerts !== "0") {
      return { label: "Due reason", value: `Health alerts: ${healthAlerts}` };
    }
  }

  for (const field of DUE_CONTEXT_FIELDS) {
    const value = readFormattedRecordValue(records, [field.key]);
    if (value) {
      const formatted = formatDueContextValue(value) ?? value;
      return { label: "Due reason", value: `${field.label}: ${formatted}` };
    }
  }

  return null;
}

function getTaskOperatorBrief(task: TaskSummary): Array<{ label: string; value: string }> {
  if (task.operatorReadModel?.operatorBrief?.length) {
    return task.operatorReadModel.operatorBrief;
  }

  const records = getTaskContextRecords(task);
  const campaign = readFormattedRecordValue(records, ["campaign", "campaignName", "sourceCampaign"]);
  const channel = readFormattedRecordValue(records, ["channel", "sourceChannel"]);
  const dueReason = readTaskDueReason(task);
  const entries = [
    { label: "For", value: readTaskTargetLabel(task) },
    { label: "Signal", value: readTaskSignalLabel(task) },
    dueReason,
    { label: "Context", value: readTaskScoreIntentLabel(task) },
    { label: "Channel", value: channel ? (formatTaskLabel(channel) ?? channel) : null },
    { label: "Campaign", value: campaign },
  ];

  return entries
    .filter((entry): entry is { label: string; value: string } => entry !== null && Boolean(entry.value))
    .slice(0, 5);
}

function getMetadataHighlights(metadata: Record<string, unknown>): Array<{ label: string; value: string }> {
  return Object.entries(metadata)
    .filter(([key]) => CONTEXT_METADATA_KEYS.has(key) && !isHiddenTaskContextKey(key))
    .map(([key, value]) => ({
      label: formatTaskLabel(key) ?? key,
      value: formatMetadataValue(value) ?? "",
    }))
    .filter((entry) => entry.value.length > 0)
    .slice(0, 6);
}

function getContextSnapshotHighlights(snapshot: Record<string, unknown>): Array<{ label: string; value: string }> {
  return Object.entries(snapshot)
    .filter(([key]) => !isHiddenTaskContextKey(key))
    .map(([key, value]) => ({
      label: formatTaskLabel(key) ?? key,
      value: formatMetadataValue(value) ?? "",
    }))
    .filter((entry) => entry.value.length > 0)
    .slice(0, 8);
}

function isHiddenTaskContextKey(key: string): boolean {
  return HIDDEN_TASK_SURFACE_KEYS.has(key) || PRIVATE_TASK_CONTEXT_KEY_PATTERN.test(key);
}

function formatCompletionDetailValue(
  value: unknown,
  field?: CrmTaskCompletionFieldDefinition,
): string | null {
  if (field?.type === "date" && (typeof value === "string" || value instanceof Date)) {
    const date = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(date.getTime())) return formatMonthDay(date);
  }
  if (typeof value === "string") {
    return formatTaskLabel(value) ?? value;
  }
  return formatMetadataValue(value);
}

function getCompletionDetailEntries(task: TaskSummary): Array<{ label: string; value: string }> {
  const details = task.metadata?.completionDetails;
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return [];
  }

  const record = details as Record<string, unknown>;
  const fieldDefinitions = new Map(
    (getCrmTaskDefinition(task.type).completionFields ?? []).map((field) => [field.key, field]),
  );
  const orderedKeys = [
    ...fieldDefinitions.keys(),
    ...Object.keys(record).filter((key) => !fieldDefinitions.has(key)),
  ];

  return orderedKeys
    .map((key) => ({
      label: fieldDefinitions.get(key)?.label ?? formatTaskLabel(key) ?? key,
      value: formatCompletionDetailValue(record[key], fieldDefinitions.get(key)) ?? "",
    }))
    .filter((entry) => entry.value.length > 0)
    .slice(0, 12);
}

function getTaskOperationalDetailEntries(
  task: TaskSummary,
  {
    assigneeLabel,
    importedTaskTypeLabel,
    memberLabelById,
    nextActionLabel,
    recurrenceLabel,
    triggerLabel,
  }: {
    assigneeLabel: string | null;
    importedTaskTypeLabel: string | null;
    memberLabelById: Map<string, string>;
    nextActionLabel: string | null;
    recurrenceLabel: string | null;
    triggerLabel: string | null;
  },
): CrmTaskDetailEntry[] {
  return [
    buildTaskDetailEntry("Status", formatTaskLabel(task.status)),
    buildTaskDetailEntry("Created", formatMonthDay(task.createdAt)),
    buildTaskDetailEntry("Updated", formatMonthDay(task.updatedAt)),
    buildTaskDetailEntry("Completed", task.completedAt ? formatMonthDay(task.completedAt) : null),
    buildTaskDetailEntry(
      "Completed by",
      task.completedBy ? formatAssignee(task.completedBy, memberLabelById) ?? task.completedBy : null,
      true,
    ),
    buildTaskDetailEntry("Assigned to", assigneeLabel, true),
    buildTaskDetailEntry("Recurrence", recurrenceLabel),
    buildTaskDetailEntry("Rule", task.sourceRuleId, true),
    buildTaskDetailEntry("Source type", importedTaskTypeLabel),
    buildTaskDetailEntry("Next action", nextActionLabel),
    buildTaskDetailEntry("Follow-up", task.followUpDate ? formatMonthDay(task.followUpDate) : null),
    buildTaskDetailEntry("Outcome", task.outcome ? formatTaskLabel(task.outcome) ?? task.outcome : null),
    buildTaskDetailEntry("Outcome notes", task.outcomeNotes),
    buildTaskDetailEntry("Trigger", triggerLabel),
    buildTaskDetailEntry("Trigger event", task.triggerEventId, true),
  ].filter((entry): entry is CrmTaskDetailEntry => Boolean(entry));
}

function buildTaskDetailEntry(
  label: string,
  value: string | null | undefined,
  truncate = false,
): CrmTaskDetailEntry | null {
  const trimmedValue = value?.trim();
  if (!trimmedValue) return null;
  return truncate ? { label, value: trimmedValue, truncate } : { label, value: trimmedValue };
}

function getTaskAutomationResolution(task: TaskSummary): {
  title: string;
  reason: string;
  eventType: string | null;
  eventId: string | null;
} | null {
  if (task.operatorReadModel?.automationResolution) {
    return task.operatorReadModel.automationResolution;
  }

  const metadata = task.metadata ?? {};
  const autoCancelReason = formatMetadataValue(metadata.autoCancelReason);
  const resolutionEventType = formatMetadataValue(metadata.resolutionEventType)
    ?? formatMetadataValue(metadata.sourceEventType);
  const resolutionEventId = formatMetadataValue(metadata.resolutionEventId)
    ?? formatMetadataValue(metadata.sourceEventId)
    ?? task.triggerEventId;

  if (!autoCancelReason && !resolutionEventType && task.outcome !== "auto_completed") {
    return null;
  }

  const eventTypeLabel = formatTaskLabel(resolutionEventType) ?? resolutionEventType;
  return {
    title: task.outcome === "auto_completed"
      ? "Auto-completed by automation"
      : task.outcome === "cancelled" || task.status === "cancelled"
        ? "Auto-cancelled by automation"
        : "Auto-closed by automation",
    reason: autoCancelReason ?? (eventTypeLabel ? `Closed after ${eventTypeLabel}.` : "Closed by a matching CRM event."),
    eventType: eventTypeLabel,
    eventId: resolutionEventId,
  };
}

function getTaskDueDisplay(dueAt: string | null | undefined, isClosed: boolean): {
  label: string;
  tone: "default" | "info" | "warning" | "critical";
} | null {
  if (!dueAt) return null;
  const dueDate = new Date(dueAt);
  if (Number.isNaN(dueDate.getTime())) return null;
  const formattedDate = formatMonthDay(dueAt);
  if (isClosed) return { label: `Due: ${formattedDate}`, tone: "default" };

  const now = new Date();
  if (dueDate.getTime() < now.getTime()) {
    return { label: `Overdue: ${formattedDate}`, tone: "critical" };
  }

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dueDay = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()).getTime();
  const daysUntilDue = Math.round((dueDay - today) / 86_400_000);

  if (daysUntilDue === 0) return { label: `Due today: ${formattedDate}`, tone: "warning" };
  if (daysUntilDue === 1) return { label: `Due tomorrow: ${formattedDate}`, tone: "warning" };
  if (daysUntilDue <= 7) return { label: `Due in ${daysUntilDue} days: ${formattedDate}`, tone: "info" };
  return { label: `Due: ${formattedDate}`, tone: "default" };
}

function readImportedTaskTypeLabel(task: TaskSummary): string | null {
  if (task.operatorReadModel?.sourceTypeLabel) {
    return task.operatorReadModel.sourceTypeLabel;
  }

  const metadataType = readFormattedRecordValue([task.metadata ?? {}], [
    "sourceTaskType",
    "source_task_type",
    "importedTaskType",
    "legacyTaskType",
    "originalTaskType",
  ]);
  const importedType = metadataType && metadataType.trim().toLowerCase() !== task.type.toLowerCase()
    ? metadataType
    : null;
  if (importedType) {
    return formatTaskLabel(importedType) ?? importedType;
  }

  const canonicalType = normalizeCrmTaskType(task.type);
  return canonicalType && canonicalType !== task.type
    ? formatTaskLabel(task.type) ?? task.type
    : null;
}

function getDueFilterBounds(filter: DueFilter): { dueAfter?: string; dueBefore?: string } {
  if (filter === "all") return {};

  const now = new Date();
  if (filter === "overdue") {
    return { dueBefore: now.toISOString() };
  }

  if (filter === "future") {
    return { dueAfter: now.toISOString() };
  }

  const nextSevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return {
    dueAfter: now.toISOString(),
    dueBefore: nextSevenDays.toISOString(),
  };
}

function formatContactOption(contact: { email: string | null; firstName: string | null; lastName: string | null; company?: string | null }): string {
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim();
  const identity = name || contact.email || "Unnamed contact";
  return contact.company ? `${identity} (${contact.company})` : identity;
}

function formatCompanyOption(company: { name: string; domain: string | null }): string {
  return company.domain ? `${company.name} (${company.domain})` : company.name;
}

function formatDealOption(deal: { title: string; value: number | null; currency: string }): string {
  return deal.value != null ? `${deal.title} · ${deal.currency} ${deal.value}` : deal.title;
}

function formatProposalOption(proposal: Proposal): string {
  const value = proposal.totalValue != null ? ` · ${proposal.currency} ${proposal.totalValue}` : "";
  const person = proposal.personName ? ` · ${proposal.personName}` : "";
  return `${proposal.title}${value} · ${formatTaskLabel(proposal.status) ?? proposal.status}${person}`;
}

function buildSelectedOfferTaskMetadata(proposal: Proposal | undefined): Record<string, unknown> | undefined {
  if (!proposal) return undefined;
  return {
    offerTitle: proposal.title,
    offerType: proposal.offerType,
    offerStatus: proposal.status,
    paymentStatus: proposal.paymentStatus,
    offerValue: proposal.totalValue,
    currency: proposal.currency,
    proposalId: proposal.id,
    offerSelectorSource: "crm_task_create_modal",
  };
}

function formatMemberOption(member: OrganizationMemberRecord): string {
  return member.name ? `${member.name} (${member.email})` : member.email;
}

function formatAssignee(value: string | null | undefined, memberLabelById: Map<string, string>): string | null {
  if (!value) return null;
  return memberLabelById.get(value) ?? value;
}

function formatScopedRecordId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
}

function formatScopeLabelList(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

export default function TasksPage() {
  const terminology = useTerminology();
  const taskLabel = terminology.t("task");
  const taskListLabel = `${taskLabel}s`;
  const routeParams = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTaskId = routeParams.taskId ?? searchParams.get("taskId");
  const scopedPersonId = searchParams.get("personId")?.trim() ?? "";
  const scopedDealId = searchParams.get("dealId")?.trim() ?? "";
  const scopedCompanyId = searchParams.get("companyId")?.trim() ?? "";
  const relatedScopeLabels = [
    scopedPersonId ? `contact ${formatScopedRecordId(scopedPersonId)}` : null,
    scopedDealId ? `deal ${formatScopedRecordId(scopedDealId)}` : null,
    scopedCompanyId ? `company ${formatScopedRecordId(scopedCompanyId)}` : null,
  ].filter((label): label is string => Boolean(label));
  const hasRelatedRecordScope = relatedScopeLabels.length > 0;
  const [statusFilter, setStatusFilter] = useState<FilterStatus>("pending");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState("");
  const [triggerFilter, setTriggerFilter] = useState("");
  const [dueFilter, setDueFilter] = useState<DueFilter>("all");
  const [showCreate, setShowCreate] = useState(false);
  const [completingTask, setCompletingTask] = useState<TaskSummary | null>(null);
  const [skippingTask, setSkippingTask] = useState<TaskSummary | null>(null);
  const [reschedulingTaskId, setReschedulingTaskId] = useState<string | null>(null);
  const [assigneeMutationTaskId, setAssigneeMutationTaskId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedTaskFallback, setSelectedTaskFallback] = useState<TaskSummary | null>(null);
  const [isSelectedTaskFallbackLoading, setIsSelectedTaskFallbackLoading] = useState(false);
  const [selectedTaskFallbackError, setSelectedTaskFallbackError] = useState<string | null>(null);
  const { data: organizationMembers } = useOrganizationMembers();
  const memberOptions = organizationMembers ?? [];
  const memberLabelById = useMemo(
    () => new Map(memberOptions.map((member) => [member.id, formatMemberOption(member)])),
    [memberOptions],
  );

  const apiStatus = statusFilter === "all" ? undefined : statusFilter;
  const taskListParams = useMemo(() => {
    const params: Parameters<typeof useTaskList>[0] = {};
    const source = sourceFilter.trim();
    const trigger = triggerFilter.trim();
    const dueBounds = getDueFilterBounds(dueFilter);

    if (scopedPersonId) params.personId = scopedPersonId;
    if (scopedDealId) params.dealId = scopedDealId;
    if (scopedCompanyId) params.companyId = scopedCompanyId;
    if (apiStatus) params.status = apiStatus;
    if (typeFilter !== "all") params.type = typeFilter;
    if (assigneeFilter !== "all") params.assignedTo = assigneeFilter;
    if (source) params.source = source;
    if (trigger) params.triggerType = trigger;
    if (dueBounds.dueAfter) params.dueAfter = dueBounds.dueAfter;
    if (dueBounds.dueBefore) params.dueBefore = dueBounds.dueBefore;

    return params;
  }, [apiStatus, assigneeFilter, dueFilter, scopedCompanyId, scopedDealId, scopedPersonId, sourceFilter, triggerFilter, typeFilter]);
  const { tasks, total, nextCursor, isLoading, error, refetch } = useTaskList(taskListParams);
  const {
    summary: taskAccountability,
    isLoading: isAccountabilityLoading,
    error: taskAccountabilityError,
    refetch: refetchTaskAccountability,
  } = useTaskAccountability(30);
  const selectedTaskAlreadyListed = useMemo(
    () => Boolean(selectedTaskId && tasks.some((task) => task.id === selectedTaskId)),
    [selectedTaskId, tasks],
  );

  useEffect(() => {
    if (selectedTaskId) {
      setStatusFilter("all");
    }
  }, [selectedTaskId]);

  useEffect(() => {
    if (!selectedTaskId) {
      setSelectedTaskFallback(null);
      setSelectedTaskFallbackError(null);
      setIsSelectedTaskFallbackLoading(false);
      return;
    }

    if (selectedTaskAlreadyListed) {
      setSelectedTaskFallback(null);
      setSelectedTaskFallbackError(null);
      setIsSelectedTaskFallbackLoading(false);
      return;
    }

    if (isLoading) return;

    let cancelled = false;
    setIsSelectedTaskFallbackLoading(true);
    setSelectedTaskFallbackError(null);

    fetchTaskDetail(selectedTaskId)
      .then((task) => {
        if (!cancelled) setSelectedTaskFallback(task);
      })
      .catch((err) => {
        if (!cancelled) {
          setSelectedTaskFallback(null);
          setSelectedTaskFallbackError(err instanceof Error ? err.message : "Failed to load selected task");
        }
      })
      .finally(() => {
        if (!cancelled) setIsSelectedTaskFallbackLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isLoading, selectedTaskAlreadyListed, selectedTaskId]);

  const handleComplete = async (taskId: string, input: CompleteTaskInput = {}) => {
    setActionError(null);
    try {
      await completeTask(taskId, input);
      setCompletingTask(null);
      refetch();
      void refetchTaskAccountability();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to complete task";
      setActionError(message);
      throw err instanceof Error ? err : new Error(message);
    }
  };

  const handleSkip = async (taskId: string, reason: string) => {
    setActionError(null);
    try {
      await skipTask(taskId, reason);
      setSkippingTask(null);
      refetch();
      void refetchTaskAccountability();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to skip task";
      setActionError(message);
      throw err instanceof Error ? err : new Error(message);
    }
  };

  const handleReschedule = async (taskId: string, dueAt: string) => {
    setActionError(null);
    setReschedulingTaskId(taskId);
    try {
      await updateTask(taskId, { dueAt });
      refetch();
      void refetchTaskAccountability();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to reschedule task";
      setActionError(message);
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setReschedulingTaskId(null);
    }
  };

  const handleUpdateAssignee = async (taskId: string, assignedTo: string | null) => {
    setActionError(null);
    setAssigneeMutationTaskId(taskId);
    try {
      await updateTask(taskId, { assignedTo });
      refetch();
      void refetchTaskAccountability();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update task assignee";
      setActionError(message);
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setAssigneeMutationTaskId(null);
    }
  };

  const handleClearAssignee = async (taskId: string) => {
    await handleUpdateAssignee(taskId, null);
  };

  const activeReviewFilterCount = [
    typeFilter !== "all",
    assigneeFilter !== "all",
    sourceFilter.trim().length > 0,
    triggerFilter.trim().length > 0,
    dueFilter !== "all",
  ].filter(Boolean).length;

  const clearReviewFilters = () => {
    setTypeFilter("all");
    setAssigneeFilter("all");
    setSourceFilter("");
    setTriggerFilter("");
    setDueFilter("all");
  };

  const clearRelatedRecordScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("personId");
    next.delete("dealId");
    next.delete("companyId");
    setSearchParams(next, { replace: true });
  };

  // Sort: overdue first, then by due date
  const displayTasks = selectedTaskFallback && !selectedTaskAlreadyListed
    ? [selectedTaskFallback, ...tasks]
    : tasks;

  const sorted = [...displayTasks].sort((a, b) => {
    if (!a.dueAt && !b.dueAt) return 0;
    if (!a.dueAt) return 1;
    if (!b.dueAt) return -1;
    return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[var(--color-foreground)]">{taskListLabel}</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] hover:bg-[var(--color-primary-hover)] transition-colors"
        >
          <Plus className="h-4 w-4" /> New {taskLabel}
        </button>
      </div>

      <CrmTaskAccountabilityPanel
        summary={taskAccountability}
        isLoading={isAccountabilityLoading}
        error={taskAccountabilityError}
        memberLabelById={memberLabelById}
      />

      <CrmTaskStatusFilterTabs
        options={STATUS_FILTER_OPTIONS}
        value={statusFilter}
        onChange={setStatusFilter}
      />

      <CrmTaskReviewFiltersPanel
        activeFilterCount={activeReviewFilterCount}
        onClearFilters={clearReviewFilters}
        typeFilter={typeFilter}
        taskTypeOptions={TASK_TYPE_FILTER_OPTIONS}
        onTypeFilterChange={setTypeFilter}
        assigneeFilter={assigneeFilter}
        assigneeOptions={memberOptions.map((member) => ({ value: member.id, label: formatMemberOption(member) }))}
        unassignedAssigneeValue={CRM_UNASSIGNED_ASSIGNEE_FILTER_VALUE}
        onAssigneeFilterChange={setAssigneeFilter}
        sourceFilter={sourceFilter}
        onSourceFilterChange={setSourceFilter}
        triggerFilter={triggerFilter}
        onTriggerFilterChange={setTriggerFilter}
        dueFilter={dueFilter}
        dueFilterOptions={DUE_FILTER_OPTIONS}
        onDueFilterChange={setDueFilter}
        scopedQueueLabel={hasRelatedRecordScope ? formatScopeLabelList(relatedScopeLabels) : null}
        onClearScope={clearRelatedRecordScope}
        isLoading={isLoading}
        visibleCount={sorted.length}
        totalCount={total || sorted.length}
        hasNextPage={Boolean(nextCursor)}
      />

      {error && (
        <CrmTaskStatusBanner
          tone="danger"
          message={error}
          actionLabel="Retry"
          onAction={refetch}
        />
      )}

      {actionError && (
        <CrmTaskStatusBanner
          tone="danger"
          message={actionError}
          actionLabel="Dismiss"
          onAction={() => setActionError(null)}
        />
      )}

      {isSelectedTaskFallbackLoading && (
        <CrmTaskStatusBanner tone="neutral" message="Loading linked task details..." />
      )}

      {selectedTaskFallbackError && (
        <CrmTaskStatusBanner tone="warning" message="Could not load the linked task. The current queue remains usable." />
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-md" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <EmptyState
          icon="tasks"
          tier={1}
          eyebrow="Daily queue"
          title={statusFilter === "completed" ? `No completed ${taskListLabel.toLowerCase()}` : `No ${taskListLabel.toLowerCase()} yet`}
          description={`Create your first ${taskLabel.toLowerCase()} so follow-ups, calls, and handoffs land in a real queue instead of getting lost in notes.`}
          actionLabel={`Create ${taskLabel.toLowerCase()}`}
          onAction={() => setShowCreate(true)}
          supportingContent={(
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                What good looks like
              </p>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Start with one overdue follow-up, one scheduled reminder, and one owner-assigned handoff so the queue becomes useful on day one.
              </p>
            </div>
          )}
        />
      ) : (
        <div className="divide-y divide-[var(--color-border)] rounded-lg border border-[var(--color-border)]">
          {sorted.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              memberLabelById={memberLabelById}
              members={memberOptions}
              initiallyExpanded={task.id === selectedTaskId}
              onComplete={() => setCompletingTask(task)}
              onSkip={() => setSkippingTask(task)}
              onReschedule={(dueAt) => handleReschedule(task.id, dueAt)}
              onUpdateAssignee={(assignedTo) => handleUpdateAssignee(task.id, assignedTo)}
              onClearAssignee={() => handleClearAssignee(task.id)}
              isRescheduling={reschedulingTaskId === task.id}
              isClearingAssignee={assigneeMutationTaskId === task.id}
              isUpdatingAssignee={assigneeMutationTaskId === task.id}
            />
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateTaskModal
          members={memberOptions}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refetch(); void refetchTaskAccountability(); }}
        />
      )}
      {completingTask && (
        <CompleteTaskModal
          task={completingTask}
          onClose={() => setCompletingTask(null)}
          onComplete={(input) => handleComplete(completingTask.id, input)}
        />
      )}
      {skippingTask && (
        <SkipTaskModal
          task={skippingTask}
          onClose={() => setSkippingTask(null)}
          onSkip={(reason) => handleSkip(skippingTask.id, reason)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task Row
// ---------------------------------------------------------------------------

const PRIORITY_DOT: Record<string, string> = {
  urgent: "text-[var(--color-destructive)]",
  high: "text-[var(--color-accent-action)]",
  medium: "text-[var(--color-warning)]",
  low: "text-[var(--color-muted-foreground)]",
};

function buildQuickRescheduleDueAt(daysFromNow: number): string {
  const dueAt = new Date();
  dueAt.setDate(dueAt.getDate() + daysFromNow);
  dueAt.setHours(9, 0, 0, 0);
  return dueAt.toISOString();
}

function dedupeTaskActions(actions: CrmTaskAction[]): CrmTaskAction[] {
  const seen = new Set<string>();
  return actions.filter((action) => {
    if (seen.has(action.href)) return false;
    seen.add(action.href);
    return true;
  });
}

function TaskRow({
  task,
  memberLabelById,
  members,
  initiallyExpanded = false,
  onComplete,
  onSkip,
  onReschedule,
  onUpdateAssignee,
  onClearAssignee,
  isRescheduling = false,
  isClearingAssignee = false,
  isUpdatingAssignee = false,
}: {
  task: TaskSummary;
  memberLabelById: Map<string, string>;
  members: OrganizationMemberRecord[];
  initiallyExpanded?: boolean;
  onComplete: () => void;
  onSkip: () => void;
  onReschedule: (dueAt: string) => void;
  onUpdateAssignee: (assignedTo: string | null) => Promise<void>;
  onClearAssignee: () => void;
  isRescheduling?: boolean;
  isClearingAssignee?: boolean;
  isUpdatingAssignee?: boolean;
}) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [detailTask, setDetailTask] = useState<TaskSummary | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [taskHistory, setTaskHistory] = useState<TaskLifecycleEventSummary[]>([]);
  const [historyNextCursor, setHistoryNextCursor] = useState<string | null>(null);
  const [historyTotal, setHistoryTotal] = useState<number | null>(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isHistoryLoadingMore, setIsHistoryLoadingMore] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const isCompleted = task.status === "completed";
  const isClosed = isCompleted || task.status === "skipped" || task.status === "cancelled";
  const isOverdue = task.dueAt && new Date(task.dueAt) < new Date() && !isClosed;
  const recurrenceLabel = getTaskRecurrenceLabel(task.metadata);
  const context = readTaskContext(task);
  const sourceLabel = formatTaskLabel(task.source);
  const typeLabel = task.operatorReadModel?.labels.type ?? getCrmTaskDefinition(task.type).label;
  const automationResolution = getTaskAutomationResolution(task);
  const automationPolicy = task.operatorReadModel?.automationPolicy ?? null;
  const sla = task.operatorReadModel?.sla ?? null;
  const slaDetail = sla ? getCrmTaskSlaDetail(sla) : null;
  const dueDisplay = getTaskDueDisplay(task.dueAt, isClosed);
  const guidance = readTaskGuidance(task);
  const operatorBrief = getTaskOperatorBrief(task);
  const visibleOperatorBrief = operatorBrief.slice(0, 5);
  const taskActionRecords = [task.metadata, task.contextSnapshot];
  const taskProfileSectionSummary = summarizeCrmTaskProfileSections(buildCrmTaskProfileSections(taskActionRecords));
  const taskPlaybooks = buildCrmTaskPlaybooks(taskActionRecords);
  const taskPlaybookSummary = summarizeCrmTaskPlaybooks(taskPlaybooks);
  const taskExecutionChecklists = buildCrmTaskExecutionChecklists(taskActionRecords);
  const taskChecklistSummary = summarizeCrmTaskExecutionChecklists(taskExecutionChecklists);
  const quickTaskActions = dedupeTaskActions([
    ...buildCrmTaskContactChannelActions(taskActionRecords, { maxActions: 3 }),
    ...buildCrmTaskActionGroups(taskActionRecords, {
      maxGroups: 2,
      maxActionsPerGroup: 3,
    }).flatMap((group) => group.actions),
  ]).slice(0, 3);
  const contextCompletenessStatus = getCrmTaskContextCompletenessStatus(task.operatorReadModel?.contextCompleteness);
  const assigneeLabel = formatAssignee(task.assignedTo, memberLabelById);
  const contactLinkLabel = readTaskContactLinkLabel(task);
  const dealLinkLabel = readTaskDealLinkLabel(task);
  const companyLinkLabel = readTaskCompanyLinkLabel(task);
  const detailsPanelId = `task-details-${task.id}`;
  const detailsToggleLabel = `${expanded ? "Hide" : "Open"} details for ${task.title}`;
  const toggleDetails = () => setExpanded((current) => !current);

  useEffect(() => {
    if (initiallyExpanded) {
      setExpanded(true);
    }
  }, [initiallyExpanded]);

  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    setIsDetailLoading(true);
    setDetailError(null);
    setIsHistoryLoading(true);
    setIsHistoryLoadingMore(false);
    setHistoryError(null);
    setTaskHistory([]);
    setHistoryNextCursor(null);
    setHistoryTotal(null);

    fetchTaskDetail(task.id)
      .then((latestTask) => {
        if (!cancelled) setDetailTask(latestTask);
      })
      .catch((err) => {
        if (!cancelled) {
          setDetailError(err instanceof Error ? err.message : "Failed to load task detail");
        }
      })
      .finally(() => {
        if (!cancelled) setIsDetailLoading(false);
      });

    fetchTaskHistory(task.id, { limit: TASK_HISTORY_PAGE_LIMIT })
      .then((page) => {
        if (!cancelled) {
          setTaskHistory(page.items);
          setHistoryNextCursor(page.nextCursor);
          setHistoryTotal(page.total);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setHistoryError(err instanceof Error ? err.message : "Failed to load task history");
        }
      })
      .finally(() => {
        if (!cancelled) setIsHistoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [expanded, task.id]);

  const loadMoreHistory = () => {
    if (!historyNextCursor || isHistoryLoadingMore) return;
    setIsHistoryLoadingMore(true);
    setHistoryError(null);

    fetchTaskHistory(task.id, { limit: TASK_HISTORY_PAGE_LIMIT, cursor: historyNextCursor })
      .then((page) => {
        setTaskHistory((current) => [...current, ...page.items]);
        setHistoryNextCursor(page.nextCursor);
        setHistoryTotal(page.total);
      })
      .catch((err) => {
        setHistoryError(err instanceof Error ? err.message : "Failed to load more task history");
      })
      .finally(() => setIsHistoryLoadingMore(false));
  };

  const detailTaskForRender = detailTask ?? task;
  const detailRecurrenceLabel = getTaskRecurrenceLabel(detailTaskForRender.metadata);
  const detailContext = readTaskContext(detailTaskForRender);

  return (
    <div id={`task-${task.id}`} className={cn("stagger-item px-4 py-3", isOverdue && "bg-[var(--color-destructive)]/5")}>
      <div className="flex items-start gap-3">
        <CrmTaskCompletionToggleButton
          isCompleted={isCompleted}
          isClosed={isClosed}
          onComplete={onComplete}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <button
              type="button"
              onClick={toggleDetails}
              className="min-w-0 flex-1 rounded-lg px-2 py-1 text-left transition-colors hover:bg-[var(--color-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-background)]"
              aria-expanded={expanded}
              aria-controls={detailsPanelId}
              aria-label={detailsToggleLabel}
            >
              <p className={cn("text-sm font-medium text-[var(--color-foreground)]", isCompleted && "text-[var(--color-muted-foreground)] line-through")}>
                {task.title}
              </p>
              <CrmTaskOperatorPreviewCard
                context={context}
                guidance={guidance}
                briefEntries={visibleOperatorBrief}
                className="mt-2"
              />
            </button>
            <CrmTaskRowActions
              quickActions={quickTaskActions}
              hasAssignee={Boolean(assigneeLabel)}
              isClosed={isClosed}
              isClearingAssignee={isClearingAssignee}
              isRescheduling={isRescheduling}
              expanded={expanded}
              detailsPanelId={detailsPanelId}
              detailsToggleLabel={detailsToggleLabel}
              onClearAssignee={onClearAssignee}
              onRescheduleInDays={(days) => onReschedule(buildQuickRescheduleDueAt(days))}
              onComplete={onComplete}
              onToggleDetails={toggleDetails}
              onSkip={onSkip}
            />
          </div>
          <CrmTaskRowFacts
            priority={task.priority}
            priorityClassName={PRIORITY_DOT[task.priority]}
            typeLabel={typeLabel}
            sourceLabel={sourceLabel}
            automationResolved={Boolean(automationResolution)}
            automationWatching={Boolean(!isClosed && !automationResolution && automationPolicy)}
            slaLabel={sla?.label ?? null}
            slaTone={sla ? getCrmTaskSlaTone(sla) : null}
            slaDetail={slaDetail}
            contextCompletenessStatus={contextCompletenessStatus}
            profileSectionSummary={taskProfileSectionSummary}
            playbookSummary={taskPlaybookSummary}
            checklistSummary={taskChecklistSummary}
            dueDisplay={dueDisplay}
            assigneeLabel={assigneeLabel}
            recurrenceLabel={recurrenceLabel}
            contactLink={task.personId ? {
              href: `/crm/contacts/${task.personId}`,
              label: contactLinkLabel ? `Contact: ${contactLinkLabel}` : "Contact",
            } : null}
            dealLink={task.dealId ? {
              href: `/crm/deals/${task.dealId}`,
              label: dealLinkLabel ? `Deal: ${dealLinkLabel}` : "Deal",
            } : null}
            companyLink={task.companyId ? {
              href: `/crm/companies/${task.companyId}`,
              label: companyLinkLabel ? `Company: ${companyLinkLabel}` : "Company",
            } : null}
            offerLinked={Boolean(task.offerId)}
            formatLabel={formatTaskLabel}
          />
        </div>
      </div>
      {expanded && (
        <div id={detailsPanelId}>
          {isDetailLoading && !detailTask && (
            <CrmTaskStatusBanner tone="neutral" message="Loading latest task details..." density="compact" className="mt-3" />
          )}
          {detailError && (
            <CrmTaskStatusBanner tone="warning" message="Could not refresh task details. Showing the current list snapshot." density="compact" className="mt-3" />
          )}
          <TaskDetails
            task={detailTaskForRender}
            memberLabelById={memberLabelById}
            members={members}
            recurrenceLabel={detailRecurrenceLabel}
            context={detailContext}
            history={taskHistory}
            historyNextCursor={historyNextCursor}
            historyTotal={historyTotal}
            isHistoryLoading={isHistoryLoading}
            isHistoryLoadingMore={isHistoryLoadingMore}
            historyError={historyError}
            onLoadMoreHistory={loadMoreHistory}
            onUpdateAssignee={onUpdateAssignee}
            isUpdatingAssignee={isUpdatingAssignee}
          />
        </div>
      )}
    </div>
  );
}

function TaskDetails({
  task,
  memberLabelById,
  members,
  recurrenceLabel,
  context,
  history,
  historyNextCursor,
  historyTotal,
  isHistoryLoading,
  isHistoryLoadingMore,
  historyError,
  onLoadMoreHistory,
  onUpdateAssignee,
  isUpdatingAssignee,
}: {
  task: TaskSummary;
  memberLabelById: Map<string, string>;
  members: OrganizationMemberRecord[];
  recurrenceLabel: string | null;
  context: string | null;
  history: TaskLifecycleEventSummary[];
  historyNextCursor: string | null;
  historyTotal: number | null;
  isHistoryLoading: boolean;
  isHistoryLoadingMore: boolean;
  historyError: string | null;
  onLoadMoreHistory: () => void;
  onUpdateAssignee: (assignedTo: string | null) => Promise<void>;
  isUpdatingAssignee: boolean;
}) {
  const [assigneeDraftId, setAssigneeDraftId] = useState(task.assignedTo ?? "");
  const [savedAssigneeId, setSavedAssigneeId] = useState(task.assignedTo ?? "");
  const [assigneeFeedback, setAssigneeFeedback] = useState<string | null>(null);
  const metadataHighlights = getMetadataHighlights(task.metadata ?? {});
  const contextSnapshotHighlights = getContextSnapshotHighlights(task.contextSnapshot ?? {});
  const automationResolution = getTaskAutomationResolution(task);
  const sla = task.operatorReadModel?.sla ?? null;
  const escalation = task.operatorReadModel?.escalation ?? null;
  const nextActionLabel = formatTaskLabel(task.nextAction) ?? task.nextAction;
  const triggerLabel = formatTaskLabel(task.triggerType) ?? task.triggerType;
  const operatorBrief = getTaskOperatorBrief(task);
  const importedTaskTypeLabel = readImportedTaskTypeLabel(task);
  const completionDetailEntries = getCompletionDetailEntries(task);
  const automationPolicy = task.operatorReadModel?.automationPolicy ?? null;
  const contextCompleteness = task.operatorReadModel?.contextCompleteness ?? null;
  const taskProfileSections = buildCrmTaskProfileSections([task.metadata, task.contextSnapshot]);
  const taskPlaybooks = buildCrmTaskPlaybooks([task.metadata, task.contextSnapshot]);
  const taskExecutionChecklists = buildCrmTaskExecutionChecklists([task.metadata, task.contextSnapshot]);
  const taskActionRecords = [task.metadata, task.contextSnapshot];
  const taskActionGroups = [
    buildCrmTaskContactChannelActionGroup(taskActionRecords),
    ...buildCrmTaskActionGroups(taskActionRecords),
  ].filter((group): group is CrmTaskActionGroup => Boolean(group));
  const assigneeOptions = members.map((member) => ({
    id: member.id,
    label: formatMemberOption(member),
  }));
  const assigneeLabel = formatAssignee(savedAssigneeId, memberLabelById);
  const isAssigneeDraftKnown = !assigneeDraftId || members.some((member) => member.id === assigneeDraftId);
  const hasAssigneeDraftChanged = assigneeDraftId !== savedAssigneeId;
  const isClosed = task.status === "completed" || task.status === "skipped" || task.status === "cancelled";
  const templateLabel = task.template ? formatTaskLabel(task.template) ?? task.template : null;
  const operationalDetailEntries = getTaskOperationalDetailEntries(task, {
    assigneeLabel,
    importedTaskTypeLabel,
    memberLabelById,
    nextActionLabel,
    recurrenceLabel,
    triggerLabel,
  });

  useEffect(() => {
    const nextAssigneeId = task.assignedTo ?? "";
    setAssigneeDraftId(nextAssigneeId);
    setSavedAssigneeId(nextAssigneeId);
    setAssigneeFeedback(null);
  }, [task.id, task.assignedTo]);

  const handleSaveAssignee = async () => {
    await onUpdateAssignee(assigneeDraftId || null);
    setSavedAssigneeId(assigneeDraftId);
    setAssigneeFeedback(assigneeDraftId
      ? `Assignee updated to ${formatAssignee(assigneeDraftId, memberLabelById) ?? assigneeDraftId}.`
      : "Task moved to the unassigned queue.");
  };

  return (
    <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm">
      <CrmTaskContextQualityCard contextCompleteness={contextCompleteness} />
      <CrmTaskAssigneeRoutingCard
        assigneeLabel={assigneeLabel}
        assigneeDraftId={assigneeDraftId}
        assigneeOptions={assigneeOptions}
        isAssigneeDraftKnown={isAssigneeDraftKnown}
        isClosed={isClosed}
        isUpdatingAssignee={isUpdatingAssignee}
        hasAssigneeDraftChanged={hasAssigneeDraftChanged}
        feedback={assigneeFeedback}
        onAssigneeDraftChange={(assigneeId) => {
          setAssigneeDraftId(assigneeId);
          setAssigneeFeedback(null);
        }}
        onSaveAssignee={() => { void handleSaveAssignee(); }}
      />
      <CrmTaskOperatorBriefCard entries={operatorBrief} />
      <CrmTaskProfileSectionsPanel sections={taskProfileSections} />
      <CrmTaskPlaybooksPanel playbooks={taskPlaybooks} />
      <CrmTaskExecutionChecklistsPanel checklists={taskExecutionChecklists} />
      <CrmTaskActionGroupsPanel groups={taskActionGroups} />
      <CrmTaskAutomationStatusCard
        automationResolution={automationResolution}
        automationPolicy={automationPolicy}
        isClosed={isClosed}
      />
      <CrmTaskSlaEscalationPanel sla={sla} escalation={escalation} />
      <CrmTaskCompletionDetailsCard entries={completionDetailEntries} />
      <CrmTaskHistoryPanel
        history={history}
        historyNextCursor={historyNextCursor}
        historyTotal={historyTotal}
        isLoading={isHistoryLoading}
        isLoadingMore={isHistoryLoadingMore}
        error={historyError}
        memberLabelById={memberLabelById}
        onLoadMoreHistory={onLoadMoreHistory}
      />
      <div className="grid gap-3 md:grid-cols-2">
        <CrmTaskWhyExistsCard
          context={context}
          templateLabel={templateLabel}
          guidance={task.scriptTemplate}
        />
        <CrmTaskOperationalDetailsCard entries={operationalDetailEntries} />
      </div>
      <CrmTaskContextSnapshotCard entries={contextSnapshotHighlights} />
      <CrmTaskMetadataHighlightsCard entries={metadataHighlights} />
    </div>
  );
}

function CompleteTaskModal({
  task,
  onClose,
  onComplete,
}: {
  task: TaskSummary;
  onClose: () => void;
  onComplete: (input: CompleteTaskInput) => Promise<void>;
}) {
  const taskDefinition = getCrmTaskDefinition(task.type);
  const completionSchema = task.operatorReadModel?.completionSchema;
  const outcomeOptions = completionSchema?.outcomes ?? getCrmTaskCompletionOutcomeDefinitions(task.type);
  const completionFields = (completionSchema?.fields ?? taskDefinition.completionFields ?? []) as readonly CrmTaskCompletionFieldDefinition[];
  const [outcome, setOutcome] = useState(task.outcome ?? "completed");
  const [outcomeNotes, setOutcomeNotes] = useState(task.outcomeNotes ?? "");
  const [nextAction, setNextAction] = useState(task.nextAction ?? "");
  const [followUpDate, setFollowUpDate] = useState(task.followUpDate?.slice(0, 10) ?? "");
  const [createFollowUpTask, setCreateFollowUpTask] = useState(Boolean(task.followUpDate));
  const [followUpSuggestionApplied, setFollowUpSuggestionApplied] = useState(Boolean(task.followUpDate));
  const [followUpTaskType, setFollowUpTaskType] = useState<TaskCompletionFollowUpType>(completionSchema?.defaultFollowUpType ?? taskDefinition.defaultFollowUpType);
  const [followUpTaskTitle, setFollowUpTaskTitle] = useState(`Follow up: ${task.title}`);
  const [completionFieldValues, setCompletionFieldValues] = useState<Record<string, string>>(() => getInitialCompletionFieldValues(task));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const completionContext = readTaskContext(task);
  const completionGuidance = readTaskGuidance(task);
  const completionBrief = getTaskOperatorBrief(task).slice(0, 4);
  const completionActionRecords = [task.metadata, task.contextSnapshot];
  const completionActionGroups = [
    buildCrmTaskContactChannelActionGroup(completionActionRecords),
    ...buildCrmTaskActionGroups(completionActionRecords),
  ].filter((group): group is CrmTaskActionGroup => Boolean(group));
  const completionProfileSections = buildCrmTaskProfileSections(completionActionRecords);
  const completionPlaybooks = buildCrmTaskPlaybooks(completionActionRecords);
  const completionChecklists = buildCrmTaskExecutionChecklists(completionActionRecords);
  const visibleCompletionFields = getVisibleCompletionFields(completionFields, outcome);
  const completionDetails = sanitizeCompletionDetails(completionFieldValues, visibleCompletionFields);
  const derivedCompletionOutcome = deriveOutcomeFromCompletionDetails(outcome, completionDetails);
  const selectedOutcomeSuggestsFollowUp = outcomeOptions.some((option) => option.value === derivedCompletionOutcome && option.suggestsFollowUp);
  const derivedOutcomeDefinition = outcomeOptions.find((option) => option.value === derivedCompletionOutcome);
  const outcomeNotesRequired = Boolean(derivedOutcomeDefinition?.requiresNotes);

  useEffect(() => {
    if (!selectedOutcomeSuggestsFollowUp || followUpSuggestionApplied || followUpDate) return;
    setFollowUpDate(getSuggestedFollowUpDateInput());
    setCreateFollowUpTask(true);
    setFollowUpSuggestionApplied(true);
  }, [followUpDate, followUpSuggestionApplied, selectedOutcomeSuggestsFollowUp]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedOutcomeNotes = outcomeNotes.trim();
    if (outcomeNotesRequired && !trimmedOutcomeNotes) {
      setCompleteError(`${derivedOutcomeDefinition?.label ?? "This outcome"} requires notes.`);
      return;
    }
    const missingRequiredField = visibleCompletionFields.find((field) => (
      isCrmTaskCompletionFieldRequiredForOutcome(field, outcome) && !completionDetails[field.key]?.trim()
    ));
    if (missingRequiredField) {
      setCompleteError(`${missingRequiredField.label} is required for this outcome.`);
      return;
    }

    setIsSubmitting(true);
    setCompleteError(null);
    try {
      const followUpAt = followUpDate ? new Date(followUpDate).toISOString() : undefined;
      await onComplete({
        outcome: derivedCompletionOutcome.trim() || undefined,
        outcomeNotes: trimmedOutcomeNotes || undefined,
        nextAction: nextAction.trim() || undefined,
        followUpDate: followUpAt,
        metadata: {
          completionSource: "crm_task_dialog",
          followUpTaskRequested: createFollowUpTask && Boolean(followUpAt),
          ...(Object.keys(completionDetails).length > 0 ? { completionDetails } : {}),
        },
        followUpTask: createFollowUpTask && followUpAt
          ? {
            title: followUpTaskTitle.trim() || `Follow up: ${task.title}`,
            type: followUpTaskType,
            priority: task.priority,
            dueAt: followUpAt,
            notes: trimmedOutcomeNotes || undefined,
          }
          : undefined,
      });
    } catch (err) {
      setCompleteError(err instanceof Error ? err.message : "Failed to complete task");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CrmTaskDialogShell
      title="Complete task"
      subtitle={task.title}
      closeLabel="Close completion dialog"
      onClose={onClose}
      onSubmit={handleSubmit}
      maxWidthClassName="max-w-lg"
    >
      <CrmTaskDialogErrorBanner message={completeError} />
      <CrmTaskCompletionContextCard
        context={completionContext}
        guidance={completionGuidance}
        brief={completionBrief}
      />
      <CrmTaskProfileSectionsPanel sections={completionProfileSections} />
      <CrmTaskActionGroupsPanel groups={completionActionGroups} />
      <CrmTaskPlaybooksPanel playbooks={completionPlaybooks} />
      <CrmTaskExecutionChecklistsPanel checklists={completionChecklists} />
      <div className="space-y-3">
        <CrmTaskCompletionOutcomeFieldsPanel
          outcome={outcome}
          outcomeOptions={outcomeOptions}
          fields={visibleCompletionFields}
          values={completionFieldValues}
          outcomeNotes={outcomeNotes}
          outcomeNotesRequired={outcomeNotesRequired}
          onOutcomeChange={setOutcome}
          onFieldValueChange={(key, value) => {
            setCompletionFieldValues((current) => ({ ...current, [key]: value }));
          }}
          onOutcomeNotesChange={setOutcomeNotes}
        />
        <CrmTaskCompletionFollowUpPanel
          nextAction={nextAction}
          followUpDate={followUpDate}
          followUpPresets={COMPLETION_FOLLOW_UP_PRESETS}
          createFollowUpTask={createFollowUpTask}
          followUpTaskTitle={followUpTaskTitle}
          followUpTaskType={followUpTaskType}
          followUpTaskTypeOptions={COMPLETION_FOLLOW_UP_TYPES}
          onNextActionChange={setNextAction}
          onFollowUpDateChange={setFollowUpDate}
          onFollowUpPresetSelect={(preset) => {
            setFollowUpDate(getFollowUpDateInputForDays(preset.days));
            setCreateFollowUpTask(true);
            setFollowUpSuggestionApplied(true);
          }}
          onCreateFollowUpTaskChange={setCreateFollowUpTask}
          onFollowUpTaskTitleChange={setFollowUpTaskTitle}
          onFollowUpTaskTypeChange={(value) => setFollowUpTaskType(value as TaskCompletionFollowUpType)}
        />
      </div>
      <CrmTaskDialogFooter
        onCancel={onClose}
        submitLabel="Complete task"
        submittingLabel="Completing..."
        isSubmitting={isSubmitting}
      />
    </CrmTaskDialogShell>
  );
}

function SkipTaskModal({
  task,
  onClose,
  onSkip,
}: {
  task: TaskSummary;
  onClose: () => void;
  onSkip: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [skipError, setSkipError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!reason.trim()) return;
    setIsSubmitting(true);
    setSkipError(null);
    try {
      await onSkip(reason.trim());
    } catch (err) {
      setSkipError(err instanceof Error ? err.message : "Failed to skip task");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CrmTaskDialogShell
      title="No longer needed"
      subtitle={task.title}
      closeLabel="Close skip dialog"
      onClose={onClose}
      onSubmit={handleSubmit}
      maxWidthClassName="max-w-md"
    >
      <CrmTaskDialogErrorBanner message={skipError} />
      <CrmTaskSkipReasonPanel reason={reason} onReasonChange={setReason} />
      <CrmTaskDialogFooter
        onCancel={onClose}
        submitLabel="Mark as not needed"
        submittingLabel="Saving..."
        isSubmitting={isSubmitting}
        submitDisabled={!reason.trim()}
      />
    </CrmTaskDialogShell>
  );
}

// ---------------------------------------------------------------------------
// Create Task Modal
// ---------------------------------------------------------------------------

function CreateTaskModal({
  members,
  onClose,
  onCreated,
}: {
  members: OrganizationMemberRecord[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const terminology = useTerminology();
  const taskLabel = terminology.t("task");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [taskType, setTaskType] = useState<TaskCompletionFollowUpType>("follow_up");
  const [dueAt, setDueAt] = useState("");
  const [priority, setPriority] = useState("medium");
  const [assignedTo, setAssignedTo] = useState("");
  const [personId, setPersonId] = useState("");
  const [dealId, setDealId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [offerId, setOfferId] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [offerSearch, setOfferSearch] = useState("");
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<RecurrenceOption>("none");
  const [recurrenceInterval, setRecurrenceInterval] = useState("1");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const {
    contacts: relatedContactOptions,
    isLoading: isLoadingRelatedContacts,
    error: relatedContactsError,
  } = useContactList({ search: contactSearch.trim() || undefined, limit: 20 });
  const {
    companies: relatedCompanyOptions,
    isLoading: isLoadingRelatedCompanies,
    error: relatedCompaniesError,
  } = useCompanyList({ search: companySearch.trim() || undefined, limit: 20 });
  const {
    deals: relatedDealOptions,
    isLoading: isLoadingRelatedDeals,
    error: relatedDealsError,
  } = useDealList();
  const {
    proposals: relatedOfferOptions,
    isLoading: isLoadingRelatedOffers,
  } = useProposals({ search: offerSearch.trim() || undefined });
  const selectedOffer = relatedOfferOptions.find((proposal) => proposal.id === offerId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setIsSubmitting(true);
    setCreateError(null);
    try {
      await createTask({
        title: title.trim(),
        description: description.trim() || undefined,
        type: taskType,
        dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
        priority,
        assignedTo: assignedTo.trim() || undefined,
        personId: personId.trim() || undefined,
        dealId: dealId.trim() || undefined,
        companyId: companyId.trim() || undefined,
        offerId: offerId.trim() || undefined,
        metadata: buildSelectedOfferTaskMetadata(selectedOffer),
        recurrence: recurrenceFrequency === "none"
          ? undefined
          : {
            frequency: recurrenceFrequency,
            interval: Number(recurrenceInterval) > 0 ? Number(recurrenceInterval) : 1,
          },
      });
      onCreated();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create task";
      setCreateError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CrmTaskDialogShell
      title={`New ${taskLabel}`}
      closeLabel="Close"
      onClose={onClose}
      onSubmit={handleSubmit}
      maxWidthClassName="max-w-xl"
    >
      <CrmTaskDialogErrorBanner message={createError} />
      <div className="space-y-3">
        <CrmTaskCreateBasicsPanel
          title={title}
          description={description}
          taskType={taskType}
          taskTypeOptions={CREATE_TASK_TYPE_OPTIONS}
          assignedTo={assignedTo}
          memberOptions={members.map((member) => ({ value: member.id, label: formatMemberOption(member) }))}
          onTitleChange={setTitle}
          onDescriptionChange={setDescription}
          onTaskTypeChange={(value) => {
            const nextType = value as TaskCompletionFollowUpType;
            setTaskType(nextType);
            setPriority(getCrmTaskDefinition(nextType).defaultPriority);
          }}
          onAssignedToChange={setAssignedTo}
        />
        <CrmTaskCreateRelatedRecordsPanel
          contactSearch={contactSearch}
          personId={personId}
          contactOptions={relatedContactOptions.map((contact) => ({ value: contact.id, label: formatContactOption(contact) }))}
          isLoadingContacts={isLoadingRelatedContacts}
          contactsError={Boolean(relatedContactsError)}
          dealId={dealId}
          dealOptions={relatedDealOptions.map((deal) => ({ value: deal.id, label: formatDealOption(deal) }))}
          isLoadingDeals={isLoadingRelatedDeals}
          dealsError={Boolean(relatedDealsError)}
          companySearch={companySearch}
          companyId={companyId}
          companyOptions={relatedCompanyOptions.map((company) => ({ value: company.id, label: formatCompanyOption(company) }))}
          isLoadingCompanies={isLoadingRelatedCompanies}
          companiesError={Boolean(relatedCompaniesError)}
          offerSearch={offerSearch}
          offerId={offerId}
          offerOptions={relatedOfferOptions.map((proposal) => ({ value: proposal.id, label: formatProposalOption(proposal) }))}
          isLoadingOffers={isLoadingRelatedOffers}
          selectedOfferContext={selectedOffer ? {
            offerType: selectedOffer.offerType,
            status: selectedOffer.status,
            paymentStatus: selectedOffer.paymentStatus,
          } : null}
          onContactSearchChange={setContactSearch}
          onPersonIdChange={setPersonId}
          onDealIdChange={setDealId}
          onCompanySearchChange={setCompanySearch}
          onCompanyIdChange={setCompanyId}
          onOfferSearchChange={setOfferSearch}
          onOfferIdChange={(nextOfferId) => {
            const nextOffer = relatedOfferOptions.find((proposal) => proposal.id === nextOfferId);
            setOfferId(nextOfferId);
            if (nextOffer?.personId && !personId.trim()) setPersonId(nextOffer.personId);
            if (nextOffer?.dealId && !dealId.trim()) setDealId(nextOffer.dealId);
          }}
        />
        <CrmTaskCreateSchedulePanel
          dueAt={dueAt}
          priority={priority}
          recurrenceFrequency={recurrenceFrequency}
          recurrenceInterval={recurrenceInterval}
          onDueAtChange={setDueAt}
          onPriorityChange={setPriority}
          onRecurrenceFrequencyChange={(value) => setRecurrenceFrequency(value as RecurrenceOption)}
          onRecurrenceIntervalChange={setRecurrenceInterval}
        />
      </div>
      <CrmTaskDialogFooter
        onCancel={onClose}
        submitLabel="Create"
        submittingLabel="Creating..."
        isSubmitting={isSubmitting}
        submitDisabled={!title.trim()}
        className="mt-4"
      />
    </CrmTaskDialogShell>
  );
}
